const GOOGLE_ANALYTICS_ID = "G-69J5LG0DGF";
const MATOMO_BASE_URL = "https://matomo.adapthabitat.fr";
const MATOMO_SITE_ID = "1";
const META_PIXEL_ID = "330563630876256";

const TRACKING_ENABLED = process.env.NODE_ENV === "production";

type ConsentState = {
  analytics: boolean;
  marketing: boolean;
};

type RouteLocation = Pick<Location, "pathname" | "search" | "hash">;
type DataLayerItem = unknown[] | IArguments;
type Gtag = (...args: unknown[]) => void;
type MatomoQueue = unknown[][];
type MetaPixel = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  loaded?: boolean;
  push?: MetaPixel;
  queue?: unknown[][];
  version?: string;
};

declare global {
  interface Window {
    dataLayer?: DataLayerItem[];
    gtag?: Gtag;
    _paq?: MatomoQueue;
    fbq?: MetaPixel;
    _fbq?: MetaPixel;
  }
}

let consentState: ConsentState = { analytics: false, marketing: false };
let googleInitialized = false;
let matomoInitialized = false;
let metaInitialized = false;
let consentWasApplied = false;
let lastAnalyticsPage = "";
let lastMarketingPage = "";

const isBrowser = () => typeof window !== "undefined";

const loadScript = (id: string, source: string) => {
  if (document.getElementById(id)) return;

  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = source;
  document.head.appendChild(script);
};

const getPage = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

const getPageUrl = () => `${window.location.origin}${getPage()}`;

const ensureGoogleAnalytics = () => {
  if (googleInitialized) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer?.push(arguments);
  };

  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    personalization_storage: "denied",
    security_storage: "granted",
  });
  window.gtag("js", new Date());
  window.gtag("config", GOOGLE_ANALYTICS_ID, {
    anonymize_ip: true,
    send_page_view: false,
  });

  loadScript(
    "adapt-habitat-google-analytics",
    `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`,
  );
  googleInitialized = true;
};

const updateGoogleConsent = ({ analytics, marketing }: ConsentState) => {
  if (!googleInitialized || !window.gtag) return;

  window.gtag("consent", "update", {
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
    analytics_storage: analytics ? "granted" : "denied",
    personalization_storage: marketing ? "granted" : "denied",
  });
};

const ensureMatomo = () => {
  window._paq = window._paq || [];

  if (matomoInitialized) {
    window._paq.push(["setConsentGiven"]);
    return;
  }

  window._paq.push(["setTrackerUrl", `${MATOMO_BASE_URL}/matomo.php`]);
  window._paq.push(["setSiteId", MATOMO_SITE_ID]);
  window._paq.push(["requireConsent"]);
  window._paq.push(["setConsentGiven"]);
  window._paq.push(["enableLinkTracking"]);

  loadScript("adapt-habitat-matomo", `${MATOMO_BASE_URL}/matomo.js`);
  matomoInitialized = true;
};

const revokeMatomoConsent = () => {
  if (!matomoInitialized || !window._paq) return;

  window._paq.push(["forgetConsentGiven"]);
  window._paq.push(["deleteCookies"]);
};

const ensureMetaPixel = () => {
  if (metaInitialized) {
    window.fbq?.("consent", "grant");
    return;
  }

  const fbq: MetaPixel = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue?.push(args);
    }
  };

  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
  window._fbq = fbq;

  fbq("init", META_PIXEL_ID);
  fbq("consent", "grant");
  loadScript(
    "adapt-habitat-meta-pixel",
    "https://connect.facebook.net/fr_FR/fbevents.js",
  );
  metaInitialized = true;
};

const trackAnalyticsPage = () => {
  const page = getPage();
  if (!consentState.analytics || page === lastAnalyticsPage) return;

  window.gtag?.("event", "page_view", {
    page_location: getPageUrl(),
    page_path: page,
    page_title: document.title,
  });

  window._paq?.push(["setCustomUrl", getPageUrl()]);
  window._paq?.push(["setDocumentTitle", document.title]);
  window._paq?.push(["trackPageView"]);
  lastAnalyticsPage = page;
};

const trackMarketingPage = () => {
  const page = getPage();
  if (!consentState.marketing || page === lastMarketingPage) return;

  window.fbq?.("track", "PageView");
  lastMarketingPage = page;
};

const trackCurrentPage = () => {
  trackAnalyticsPage();
  trackMarketingPage();
};

export const applyTrackingConsent = (nextState: ConsentState) => {
  if (!isBrowser()) return;

  const previousState = consentState;
  const unchanged =
    consentWasApplied &&
    previousState.analytics === nextState.analytics &&
    previousState.marketing === nextState.marketing;

  consentState = nextState;
  consentWasApplied = true;

  if (!TRACKING_ENABLED || unchanged) return;

  if (nextState.analytics) {
    ensureGoogleAnalytics();
    updateGoogleConsent(nextState);
    ensureMatomo();
  } else if (previousState.analytics) {
    updateGoogleConsent(nextState);
    revokeMatomoConsent();
    lastAnalyticsPage = "";
  }

  if (googleInitialized && previousState.marketing !== nextState.marketing) {
    updateGoogleConsent(nextState);
  }

  if (nextState.marketing) {
    ensureMetaPixel();
  } else if (previousState.marketing) {
    window.fbq?.("consent", "revoke");
    lastMarketingPage = "";
  }

  trackCurrentPage();
};

export const trackRouteChange = (_location: RouteLocation) => {
  if (!isBrowser() || !TRACKING_ENABLED) return;

  window.setTimeout(trackCurrentPage, 0);
};
